/**
 * API client.
 *
 * Wraps fetch with same-origin credentials, the double-submit CSRF header and
 * RFC 9457 problem+json parsing. State-changing requests automatically echo the
 * non-HttpOnly `csrf_token` cookie as `X-CSRF-Token`; `ifMatch` sets the
 * `If-Match` ETag used for optimistic concurrency on the load ledger.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const CSRF_COOKIE = "csrf_token";
const CSRF_HEADER = "X-CSRF-Token";
const STATE_CHANGING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

let _refreshing: Promise<boolean> | null = null;

/** Rotate the refresh cookie once; concurrent callers share the same attempt. */
async function tryRefresh(): Promise<boolean> {
  if (_refreshing) return _refreshing;
  _refreshing = (async () => {
    try {
      // The refresh POST is a state-changing request: it must carry the CSRF
      // token, otherwise the CSRF middleware rejects it with 403 and the
      // session is dropped mid-use.
      const token = getCookie(CSRF_COOKIE);
      const headers = new Headers();
      if (token) headers.set(CSRF_HEADER, token);
      const response = await fetch("/api/v1/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers,
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      _refreshing = null;
    }
  })();
  return _refreshing;
}

/** Redirect to the login page when the session can no longer be refreshed. */
function redirectToLogin(): void {
  if (window.location.pathname === "/login") return;
  window.location.assign("/login");
}

export interface ApiRequestInit extends RequestInit {
  /** When set, sends `If-Match: "<value>"` for optimistic concurrency. */
  ifMatch?: string | number;
}

export async function apiFetch<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const attempt = async (): Promise<Response> => {
    const headers = new Headers(init.headers);
    // FormData uploads get their multipart content-type + boundary from the
    // browser; forcing JSON here would break them.
    if (
      init.body !== undefined &&
      !(init.body instanceof FormData) &&
      !headers.has("Content-Type")
    ) {
      headers.set("Content-Type", "application/json");
    }
    if (init.ifMatch !== undefined) {
      headers.set("If-Match", `"${init.ifMatch}"`);
    }
    if (STATE_CHANGING.has(method)) {
      const token = getCookie(CSRF_COOKIE);
      if (token) headers.set(CSRF_HEADER, token);
    }
    const { ifMatch: _ifMatch, ...rest } = init;
    void _ifMatch;
    return fetch(`/api/v1${path}`, {
      credentials: "include",
      headers,
      ...rest,
    });
  };

  let response = await attempt();
  if (response.status === 401 && !path.startsWith("/auth/login") && !path.startsWith("/auth/refresh")) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      response = await attempt();
    } else if (path !== "/auth/me") {
      // Session is truly expired — send the user back to the login page.
      redirectToLogin();
    }
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      detail?: string;
    } | null;
    throw new ApiError(
      response.status,
      body?.code ?? "ERR_UNKNOWN",
      body?.detail ?? response.statusText,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

